import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react"
import type { PageEditorHandle } from "./page-editor"
import { Markdown } from "./markdown"
import { SegmentedControl } from "./ui"

const PageEditor = lazy(() => import("./page-editor"))

// A markdown field, block editor and all, for places that are not a wiki page.
//
// Two things it exists to solve, both of which come from there being many of
// these on a screen rather than one:
//
//   1. **Nothing loads until someone writes.** The editor module is ~1.5 MB
//      plus a hundred CodeMirror language chunks, and a task page can hold one
//      of these for the description plus one per comment being edited. So the
//      resting state is rendered markdown — no Crepe, no ProseMirror, no Vue —
//      and the editor is mounted by the first click. A comment thread nobody
//      writes in costs nothing.
//   2. **One copy of the fallback.** The wiki keeps its Blocks/Markdown toggle
//      and its "the editor broke, switch to markdown" recovery in the *page*
//      (wiki-page.tsx). Two more call sites would be two more copies of it.
//
// `initialValue` is read once, like the editor's own: change it by remounting
// with a `key`. That is also how a comment box clears itself after posting.

export type MarkdownFieldHandle = {
  /** What is in the field right now, editor or textarea. Read on save rather
   *  than trusting the debounced onChange. */
  getValue: () => string
}

export function MarkdownField({
  initialValue,
  onChange,
  handle,
  placeholder = "Write, or press / for blocks",
  onUpload,
  proxyDomURL,
  resolveHref,
  onMentionSearch,
  compact = false,
  /** Mount the editor at a height worth writing a description in. */
  tall = false,
  /** Skip the resting state and mount the editor immediately — for a box that
   *  appeared *because* someone chose to edit. */
  startEditing = false,
  onKeyDown,
  ariaLabel,
}: {
  initialValue: string
  onChange?: (markdown: string) => void
  handle?: React.MutableRefObject<MarkdownFieldHandle | null>
  placeholder?: string
  onUpload?: (file: File) => Promise<string>
  /** How a stored image `src` is turned into something an `<img>` can load —
   *  see PageEditor. */
  proxyDomURL?: (url: string) => string
  /** How a stored image `src` resolves when *showing* the resting render —
   *  the display twin of proxyDomURL, fed the same attachment list. */
  resolveHref?: (url: string) => string | null
  /** Candidates for the `@` menu. Absent means no mention menu — descriptions
   *  use this editor too and do not mention anyone. */
  onMentionSearch?: (query: string) => Promise<Array<{ id: string; name: string }>>
  compact?: boolean
  tall?: boolean
  startEditing?: boolean
  onKeyDown?: (event: React.KeyboardEvent) => void
  ariaLabel?: string
}) {
  const [live, setLive] = useState(startEditing)
  const [mode, setMode] = useState<"blocks" | "markdown">("blocks")
  const [value, setValue] = useState(initialValue)
  const editor = useRef<PageEditorHandle | null>(null)

  // Falling back is not a failure the author should have to recover from — the
  // markdown editor edits the same bytes.
  const [broken, setBroken] = useState(false)
  useEffect(() => {
    if (broken) setMode("markdown")
  }, [broken])

  const read = useCallback(
    () => (mode === "blocks" ? (editor.current?.getMarkdown() ?? value) : value),
    [mode, value],
  )

  useEffect(() => {
    if (!handle) return
    handle.current = { getValue: read }
    return () => {
      handle.current = null
    }
  }, [handle, read])

  const emit = useCallback(
    (next: string) => {
      setValue(next)
      onChange?.(next)
    },
    [onChange],
  )

  if (!live) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={() => setLive(true)}
        className={`w-full cursor-text rounded-lg border border-border bg-transparent p-3 text-left transition-colors hover:border-border-strong ${tall ? "min-h-64" : ""}`}
      >
        {value.trim() ? (
          <Markdown resolveHref={resolveHref}>{value}</Markdown>
        ) : (
          <span className="text-sm text-muted">{placeholder}</span>
        )}
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-2" onKeyDown={onKeyDown}>
      <div className="flex items-center gap-2">
        <SegmentedControl
          ariaLabel="Editor"
          value={mode}
          onChange={(next) => {
            // Carry the current text across, so switching does not lose work.
            if (mode === "blocks") setValue(editor.current?.getMarkdown() ?? value)
            setMode(next)
          }}
          options={[
            { value: "blocks", label: "Blocks" },
            { value: "markdown", label: "Markdown" },
          ]}
        />
        <span className="text-xs text-faint">
          {mode === "blocks" ? "Press / for blocks" : "Raw markdown"}
        </span>
      </div>

      <div className="rounded-lg border border-border p-3">
        {mode === "blocks" ? (
          <Suspense
            fallback={<div className="min-h-[4.5rem] text-sm text-faint">Loading the editor…</div>}
          >
            <PageEditor
              initialValue={value}
              handle={editor}
              onChange={emit}
              onError={() => setBroken(true)}
              onUpload={onUpload}
              proxyDomURL={proxyDomURL}
              onMentionSearch={onMentionSearch}
              placeholder={placeholder}
              compact={compact}
              tall={tall}
              autoFocus
            />
          </Suspense>
        ) : (
          <textarea
            value={value}
            autoFocus
            aria-label={ariaLabel ?? "Markdown"}
            onChange={(event) => emit(event.target.value)}
            placeholder={placeholder}
            className={`w-full resize-y bg-transparent font-mono text-xs text-foreground outline-none placeholder:text-muted ${tall ? "min-h-64" : "min-h-[4.5rem]"}`}
          />
        )}
      </div>
    </div>
  )
}
